import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    plus: ['M12 5v14', 'M5 12h14'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    userPlus: ['M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M19 8v6', 'M16 11h6'],
    chevronRight: ['M9 18l6-6-6-6'],
    loader: ['M12 2v4', 'M12 18v4', 'M4.93 4.93l2.83 2.83', 'M16.24 16.24l2.83 2.83', 'M2 12h4', 'M18 12h4', 'M4.93 19.07l2.83-2.83', 'M16.24 7.76l2.83-2.83'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z']
};

export default function PassengerAllocation({ user }) {
    const [allocations, setAllocations] = useState([]);
    const [clients, setClients] = useState([]);
    const [trips, setTrips] = useState([]);
    const [passengers, setPassengers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(null);

    const operatorId = user?.operatorId || user?._id || 'all';

    useEffect(() => {
        fetchData();
        fetchCommonData();
    }, []);

    const fetchCommonData = async () => {
        try {
            const [clientRes, tripRes, passengerRes] = await Promise.all([
                axios.get(`${API}/clients/${operatorId}`),
                axios.get(`${API}/trips/${operatorId}`),
                axios.get(`${API}/passengers/${operatorId}`)
            ]);
            setClients(clientRes.data);
            setTrips(tripRes.data);
            setPassengers(passengerRes.data);
        } catch (err) { console.error(err); }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/passenger-allocations/${operatorId}`);
            setAllocations(res.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleSave = async (allocs) => {
        try {
            await Promise.all(allocs.map(a => {
                const payload = { ...a, operatorId };

                // Sanitize IDs (Mongoose fails on empty strings for ObjectId)
                const sanitizeId = (id) => {
                    if (!id) return null;
                    if (typeof id === 'object') return id._id || null;
                    return id === '' ? null : id;
                };

                payload.passengerId = sanitizeId(payload.passengerId);
                payload.tripId = sanitizeId(payload.tripId);
                payload.pickupStopId = sanitizeId(payload.pickupStopId);
                payload.dropStopId = sanitizeId(payload.dropStopId);

                if (a._id) {
                    return axios.put(`${API}/passenger-allocations/${a._id}`, payload);
                }
                return axios.post(`${API}/passenger-allocations`, payload);
            }));
            alert(allocs[0]?._id ? 'Allocation updated!' : 'Passengers allocated successfully!');
            setModal(false);
            fetchData();
        } catch (err) {
            console.error('Error saving allocation:', err);
            alert(`Failed to save: ${err.response?.data?.message || err.message}`);
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API}/passenger-allocations/${deleteModal._id}`);
            setDeleteModal(null);
            fetchData();
        } catch (err) { console.error(err); }
    };

    const filtered = allocations.filter(a =>
        a.passengerId?.name?.toLowerCase().includes(search.toLowerCase()) ||
        a.tripId?.routeId?.name?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="md-page">
            <div className="md-header">
                <div>
                    <h1 className="md-title">Passenger Allocation</h1>
                    <p className="md-subtitle">Assign passengers to specific trips and manage their transport schedule.</p>
                </div>
                <button className="md-add-btn" onClick={() => setModal(true)}>
                    <Icon d={icons.userPlus} size={14} /> New Allocation
                </button>
            </div>

            <div className="md-panel">
                <div className="md-panel-toolbar">
                    <div className="md-search-wrap">
                        <Icon d={icons.search} size={14} />
                        <input
                            className="md-input md-search"
                            placeholder="Search by passenger or trip..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading allocations...</div>
                ) : (
                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th">Passenger</th>
                                    <th className="md-th">Client</th>
                                    <th className="md-th">Trip Detail</th>
                                    <th className="md-th">Type</th>
                                    <th className="md-th">Pickup/Drop Stops</th>
                                    <th className="md-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(a => (
                                    <tr key={a._id}>
                                        <td className="md-td">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 12, fontWeight: 700 }}>
                                                    {a.passengerId?.name?.charAt(0)}
                                                </div>
                                                <div>
                                                    <strong>{a.passengerId?.name}</strong>
                                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>ID: {a.passengerId?.employeeId}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="md-td">{a.tripId?.clientId?.companyName}</td>
                                        <td className="md-td" style={{ minWidth: 180 }}>
                                            <div style={{ fontWeight: 700, color: '#0f172a' }}>{a.tripId?.routeId?.name}</div>
                                            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <span>{a.tripId?.routeId?.pickupLocation}</span>
                                                <Icon d={icons.chevronRight} size={10} />
                                                <span>{a.tripId?.routeId?.dropLocation}</span>
                                            </div>
                                            <div style={{ fontSize: 11, color: '#6366f1', marginTop: 6, fontWeight: 500 }}>
                                                {a.tripId?.tripType} • {a.tripId?.tripFrequency}
                                            </div>
                                        </td>
                                        <td className="md-td">
                                            <span className={`md-status-badge ${a.allocationType === 'Recurring' ? 'md-badge-active' : ''}`} style={{ fontSize: 10, padding: '4px 10px' }}>
                                                {a.allocationType}
                                            </span>
                                        </td>
                                        <td className="md-td" style={{ minWidth: 220 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                <div style={{ fontSize: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                    <div style={{ width: 18, height: 18, borderRadius: 4, background: '#ecfdf5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900, marginTop: 1 }}>P</div>
                                                    <div>
                                                        {a.pickupStopId?.name ? (
                                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                <span style={{ color: '#94a3b8', fontSize: 10 }}>From {a.tripId?.routeId?.pickupLocation} to</span>
                                                                <strong style={{ color: '#0f172a' }}>{a.pickupStopId.name}</strong>
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: '#64748b' }}>{a.pickupPoint || a.tripId?.routeId?.pickupLocation || 'Route Origin'}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div style={{ fontSize: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                    <div style={{ width: 18, height: 18, borderRadius: 4, background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900, marginTop: 1 }}>D</div>
                                                    <div>
                                                        {a.dropStopId?.name ? (
                                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                <span style={{ color: '#94a3b8', fontSize: 10 }}>Then to</span>
                                                                <strong style={{ color: '#0f172a' }}>{a.dropStopId.name}</strong>
                                                                <span style={{ color: '#94a3b8', fontSize: 10 }}>ending at {a.tripId?.routeId?.dropLocation}</span>
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: '#64748b' }}>{a.dropPoint || a.tripId?.routeId?.dropLocation || 'Route Destination'}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="md-td">
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <button className="md-icon-btn" style={{ color: '#6366f1' }} onClick={() => setModal(a)} title="Edit Allocation">
                                                    <Icon d={icons.edit} size={14} />
                                                </button>
                                                <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal(a)} title="Remove Allocation">
                                                    <Icon d={icons.trash} size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="md-td" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                            No allocations found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {modal && (
                <AllocationForm
                    clients={clients}
                    trips={trips}
                    passengers={passengers}
                    onClose={() => setModal(false)}
                    onSave={handleSave}
                    operatorId={operatorId}
                    editData={typeof modal === 'object' ? modal : null}
                />
            )}

            {/* Delete Confirmation */}
            {deleteModal && (
                <div className="md-modal-overlay" onClick={() => setDeleteModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header">
                            <h3 className="md-modal-title" style={{ color: '#ef4444' }}>Confirm Removal</h3>
                        </div>
                        <div className="md-modal-body">
                            Remove <strong>{deleteModal.passengerId?.name}</strong> from this trip allocation?
                        </div>
                        <div className="md-modal-footer">
                            <button className="md-cancel-btn" onClick={() => setDeleteModal(null)}>Cancel</button>
                            <button className="md-save-btn" style={{ background: '#ef4444' }} onClick={handleDelete}>Remove</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function AllocationForm({ clients, trips, passengers, onClose, onSave, operatorId, editData }) {
    const [step, setStep] = useState(1);
    const [selectedClient, setSelectedClient] = useState(editData?.tripId?.clientId?._id || editData?.tripId?.clientId || '');
    const [selectedPassengers, setSelectedPassengers] = useState(editData ? [editData.passengerId?._id || editData.passengerId] : []);
    const [selectedTrip, setSelectedTrip] = useState(editData?.tripId?._id || editData?.tripId || '');
    const [allocationType, setAllocationType] = useState('Recurring');
    const [assignments, setAssignments] = useState([]);
    const [routeStops, setRouteStops] = useState([]);
    const [loadingStops, setLoadingStops] = useState(false);
    const [showQuickAdd, setShowQuickAdd] = useState(false);

    // Derived data
    const selectedClientObj = React.useMemo(() =>
        clients.find(c => c._id === selectedClient),
        [clients, selectedClient]);

    const filteredPassengers = React.useMemo(() =>
        selectedClientObj ? selectedClientObj.contacts.map(c => ({
            _id: c._id,
            name: `${c.firstName} ${c.lastName}`.trim() || c.email,
            email: c.email,
            contactNumber: c.primaryPhoneNumber || '',
            clientId: selectedClient,
            isContact: true
        })) : [],
        [selectedClientObj, selectedClient]);

    const filteredTrips = React.useMemo(() =>
        trips.filter(t => t.clientId?._id === selectedClient || t.clientId === selectedClient),
        [trips, selectedClient]);

    useEffect(() => {
        if (selectedTrip) {
            const trip = trips.find(t => t._id === selectedTrip);
            if (trip) {
                if (trip.routeId) {
                    fetchRouteStops(trip.routeId._id || trip.routeId);
                }
                if (trip.tripFrequency) {
                    setAllocationType(trip.tripFrequency);
                }
            }
        } else {
            setRouteStops([]);
        }
    }, [selectedTrip]);

    const fetchRouteStops = async (routeId) => {
        setLoadingStops(true);
        try {
            const res = await axios.get(`${API}/route-stops/${routeId}`);
            setRouteStops(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingStops(false);
        }
    };

    const handlePassengerToggle = (passengerId) => {
        setSelectedPassengers(prev =>
            prev.includes(passengerId)
                ? prev.filter(id => id !== passengerId)
                : [...prev, passengerId]
        );
    };

    // Initialize/Sync assignments when passengers or trip changes
    useEffect(() => {
        setAssignments(prevAssignments => {
            return selectedPassengers.map(pid => {
                const existing = prevAssignments.find(a => a.passengerId === pid);
                if (existing) return existing;

                if (editData && (editData.passengerId?._id === pid || editData.passengerId === pid)) {
                    const pName = editData.passengerId?.name || (typeof editData.passengerId === 'string' ? 'Loading...' : undefined);
                    return {
                        _id: editData._id,
                        passengerId: pid,
                        name: pName || 'Unknown',
                        pickupStopId: editData.pickupStopId?._id || editData.pickupStopId || '',
                        dropStopId: editData.dropStopId?._id || editData.dropStopId || '',
                        pickupPoint: editData.pickupPoint || '',
                        dropPoint: editData.dropPoint || '',
                        passengerInfo: typeof editData.passengerId === 'object' ? editData.passengerId : undefined
                    };
                }

                const p = filteredPassengers.find(x => x._id === pid);
                return {
                    passengerId: pid,
                    name: p?.name || 'Unknown',
                    passengerInfo: p,
                    pickupStopId: '',
                    dropStopId: '',
                    pickupPoint: '',
                    dropPoint: ''
                };
            });
        });
    }, [selectedPassengers, filteredPassengers, editData]);

    useEffect(() => {
        if (editData && editData.allocationType) {
            setAllocationType(editData.allocationType);
        }
    }, [editData]);

    const handleAssignmentChange = (pid, field, value) => {
        setAssignments(prev => prev.map(a => a.passengerId === pid ? { ...a, [field]: value } : a));
    };

    const removePassenger = (pid) => {
        setSelectedPassengers(prev => prev.filter(id => id !== pid));
    };

    const handleSubmit = () => {
        const finalAllocations = assignments.map(a => ({
            _id: a._id,
            tripId: selectedTrip,
            passengerId: a.passengerId,
            pickupStopId: a.pickupStopId || undefined,
            dropStopId: a.dropStopId || undefined,
            pickupPoint: a.pickupPoint,
            dropPoint: a.dropPoint,
            allocationType: allocationType,
            passengerInfo: a.passengerInfo
        }));
        onSave(finalAllocations);
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" style={{ maxWidth: 1100, width: '95%' }} onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h3 className="md-modal-title">Passenger Allocation Wizard</h3>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>

                <div className="md-modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '30px', padding: 25 }}>
                    {/* Left Column: Form Controls */}
                    <div className="allocation-form-left">
                        <div className="md-form-group">
                            <label className="md-label">1. Select Client*</label>
                            <select
                                className="md-input"
                                value={selectedClient}
                                disabled={!!editData}
                                onChange={e => {
                                    setSelectedClient(e.target.value);
                                    setSelectedPassengers([]);
                                    setSelectedTrip('');
                                }}
                            >
                                <option value="">Select Client</option>
                                {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                            </select>
                        </div>

                        <div className="md-form-group">
                            <label className="md-label">2. Select Passengers* {selectedPassengers.length > 0 && `(${selectedPassengers.length} selected)`}</label>
                            <div style={{
                                border: '1px solid #e2e8f0',
                                borderRadius: 12,
                                maxHeight: 180,
                                overflowY: 'auto',
                                background: (!selectedClient || !!editData) ? '#f8fafc' : '#fff',
                                opacity: (!selectedClient || !!editData) ? 0.6 : 1,
                                pointerEvents: !!editData ? 'none' : 'auto'
                            }}>
                                {!selectedClient ? (
                                    <div style={{ padding: 15, fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>Please select a client first</div>
                                ) : filteredPassengers.length === 0 ? (
                                    <div style={{ padding: 15, fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>No people found for this client. Please add them in Client Details &gt; People &amp; Contacts.</div>
                                ) : (
                                    filteredPassengers.map(p => (
                                        <label key={p._id} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '8px 12px',
                                            gap: 10,
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #f1f5f9',
                                            transition: 'background 0.2s',
                                            fontSize: 13
                                        }} className="passenger-item-hover">
                                            <input
                                                type="checkbox"
                                                checked={selectedPassengers.includes(p._id)}
                                                onChange={() => handlePassengerToggle(p._id)}
                                            />
                                            <span>{p.name} <small style={{ color: '#94a3b8' }}>({p.employeeId || p.email || 'Contact'})</small></span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="md-form-group">
                            <label className="md-label">3. Select Trip*</label>
                            <select
                                className="md-input"
                                value={selectedTrip}
                                onChange={e => setSelectedTrip(e.target.value)}
                                disabled={!selectedClient}
                                style={{ background: !selectedClient ? '#f8fafc' : '#fff' }}
                            >
                                <option value="">Select Trip</option>
                                {filteredTrips.map(t => (
                                    <option key={t._id} value={t._id}>
                                        {t.routeId?.name} ({t.tripType})
                                    </option>
                                ))}
                            </select>
                        </div>

                    </div>

                    {/* Right Column: Assignment Table */}
                    <div className="allocation-form-right" style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <label className="md-label" style={{ margin: 0 }}>Step 4: Assign Stops per Passenger</label>
                                {selectedTrip && (
                                    <button
                                        onClick={() => setShowQuickAdd(!showQuickAdd)}
                                        style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none', fontWeight: 600, border: '1px solid #e0e7ff', padding: '4px 10px', borderRadius: 8, background: '#f5f7ff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                    >
                                        <Icon d={showQuickAdd ? icons.x : icons.plus} size={12} /> {showQuickAdd ? 'Cancel Quick Add' : 'Quick Add Stop'}
                                    </button>
                                )}
                            </div>
                            {loadingStops && <div style={{ fontSize: 12, color: '#6366f1', display: 'flex', alignItems: 'center', gap: 5 }}><Icon d={icons.loader} size={14} /> Loading Stops...</div>}
                        </div>

                        {showQuickAdd && selectedTrip && (
                            <div style={{ background: '#fff', border: '1px solid #6366f1', borderRadius: 12, padding: 15, marginBottom: 15, boxShadow: '0 4px 12px rgba(99, 102, 241, 0.1)' }}>
                                <QuickAddStopForm
                                    routeId={trips.find(t => t._id === selectedTrip)?.routeId?._id || trips.find(t => t._id === selectedTrip)?.routeId}
                                    operatorId={operatorId}
                                    onSave={(newStop) => {
                                        fetchRouteStops(trips.find(t => t._id === selectedTrip)?.routeId?._id || trips.find(t => t._id === selectedTrip)?.routeId);
                                        setShowQuickAdd(false);
                                    }}
                                />
                            </div>
                        )}

                        <div style={{
                            flex: 1,
                            background: '#f8fafc',
                            borderRadius: 16,
                            border: '1px solid #e2e8f0',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {!selectedTrip ? (
                                <div style={{ margin: 'auto', textAlign: 'center', color: '#94a3b8', padding: 20 }}>
                                    <div style={{ marginBottom: 10, opacity: 0.5 }}><Icon d={icons.chevronRight} size={32} /></div>
                                    <p>Select a trip to see its route stops and assign them to passengers.</p>
                                </div>
                            ) : selectedPassengers.length === 0 ? (
                                <div style={{ margin: 'auto', textAlign: 'center', color: '#94a3b8', padding: 20 }}>
                                    <p>Select passengers to begin assignment.</p>
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="md-table" style={{ background: 'transparent' }}>
                                        <thead style={{ background: '#f1f5f9' }}>
                                            <tr>
                                                <th className="md-th" style={{ padding: '8px 12px', fontSize: 11 }}>Passenger</th>
                                                <th className="md-th" style={{ padding: '8px 12px', fontSize: 11 }}>Pickup Stop</th>
                                                <th className="md-th" style={{ padding: '8px 12px', fontSize: 11 }}>Drop Stop</th>
                                                <th className="md-th" style={{ padding: '8px 12px', fontSize: 11, width: 40 }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const activeTrip = trips.find(t => t._id === selectedTrip);
                                                const isLogin = activeTrip?.tripType === 'Login';
                                                const isLogout = activeTrip?.tripType === 'Logout';

                                                return assignments.map(a => (
                                                    <tr key={a.passengerId}>
                                                        <td className="md-td" style={{ padding: '8px 12px', fontSize: 12 }}>
                                                            <strong>{a.name}</strong>
                                                        </td>
                                                        <td className="md-td" style={{ padding: '6px' }}>
                                                            <select
                                                                className="md-input"
                                                                style={{ height: 32, fontSize: 12, padding: '0 8px', opacity: isLogout ? 0.6 : 1 }}
                                                                value={isLogout ? "" : a.pickupStopId}
                                                                disabled={isLogout}
                                                                onChange={e => handleAssignmentChange(a.passengerId, 'pickupStopId', e.target.value)}
                                                            >
                                                                <option value="">Start: {activeTrip?.routeId?.pickupLocation || 'Route Origin'}</option>
                                                                {routeStops.map(s => <option key={s._id} value={s._id}>{s.name || s.stopName}</option>)}
                                                            </select>
                                                        </td>
                                                        <td className="md-td" style={{ padding: '6px' }}>
                                                            <select
                                                                className="md-input"
                                                                style={{ height: 32, fontSize: 12, padding: '0 8px', opacity: isLogin ? 0.6 : 1 }}
                                                                value={isLogin ? "" : a.dropStopId}
                                                                disabled={isLogin}
                                                                onChange={e => handleAssignmentChange(a.passengerId, 'dropStopId', e.target.value)}
                                                            >
                                                                <option value="">End: {activeTrip?.routeId?.dropLocation || 'Route Destination'}</option>
                                                                {routeStops.map(s => <option key={s._id} value={s._id}>{s.name || s.stopName}</option>)}
                                                            </select>
                                                        </td>
                                                        <td className="md-td" style={{ padding: '6px', textAlign: 'center' }}>
                                                            <button
                                                                onClick={() => removePassenger(a.passengerId)}
                                                                style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer' }}
                                                            >
                                                                <Icon d={icons.x} size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ));
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="md-save-btn"
                        disabled={!selectedClient || !selectedTrip || selectedPassengers.length === 0}
                        onClick={handleSubmit}
                        style={{ opacity: (!selectedClient || !selectedTrip || selectedPassengers.length === 0) ? 0.6 : 1 }}
                    >
                        Allocate {selectedPassengers.length > 0 ? selectedPassengers.length : ''} Passenger{selectedPassengers.length !== 1 ? 's' : ''}
                    </button>
                </div>
            </div>
        </div>
    );
}

function QuickAddStopForm({ routeId, operatorId, onSave }) {
    const [name, setName] = useState('');
    const [lat, setLat] = useState('');
    const [lng, setLng] = useState('');
    const [loading, setLoading] = useState(false);
    const autocompleteRef = useRef(null);

    useEffect(() => {
        if (!window.google) return;
        const autocomplete = new window.google.maps.places.Autocomplete(autocompleteRef.current, { fields: ['formatted_address', 'geometry', 'name'] });
        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place.geometry) {
                setName(place.formatted_address || place.name);
                setLat(place.geometry.location.lat());
                setLng(place.geometry.location.lng());
            }
        });
    }, []);

    const handleQuickSave = async () => {
        setLoading(true);
        try {
            const res = await axios.post(`${API}/route-stops`, {
                name,
                lat: Number(lat),
                lng: Number(lng),
                routeId,
                operatorId,
                order: 999 // Put at end
            });
            onSave(res.data);
        } catch (err) {
            console.error(err);
            alert('Failed to add stop');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: 10 }}>
            <div className="md-form-group" style={{ margin: 0 }}>
                <input
                    ref={autocompleteRef}
                    className="md-input"
                    placeholder="Search new stop location..."
                    value={name}
                    onChange={e => setName(e.target.value)}
                    style={{ height: 36, fontSize: 13 }}
                />
            </div>
            <div style={{ display: 'flex', gap: 5 }}>
                <input className="md-input" style={{ height: 36, fontSize: 12 }} placeholder="Lat" value={lat} readOnly />
                <input className="md-input" style={{ height: 36, fontSize: 12 }} placeholder="Lng" value={lng} readOnly />
            </div>
            <button
                className="md-save-btn"
                style={{ height: 36, fontSize: 13, padding: 0 }}
                disabled={!name || !lat || !lng || loading}
                onClick={handleQuickSave}
            >
                {loading ? '...' : 'Save'}
            </button>
        </div>
    );
}

